import { useEffect, useState } from "react";
import { financeiroService } from "./service/financeiro.service";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Icon,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { baseApiService } from "../../shared/services/api/axios-config/axios-config";
import { FiltroMovimentacoesModal } from "../../shared/components/ModalFiltros/modal-filtros";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import {
  categorias,
  modalidade,
  tiposMovimentacao,
} from "./schema/gerar-movimentacao-obra.schema";
import { RetornoDaListagemVazia } from "../../shared/components/retornoDaListagem/retornoDaListagem";
import { formatDate, formatDateBR } from "../../shared/utils";
import { ConstructionMovementEnum } from "../../shared/utils/enum/enum";
import { ModalDetalhamentoMovimentacaoFinanceira } from "./modal-visualizar-movimentacao-financeira";
import { useLoader } from "../../shared/contexts/LoaderContext";

interface MovimentacaoObra {
  id: string;
  dateMovement: string;
  value: number;
  description: string;
  quantityToPay: number;
  category: string;
  movementType: string;
  unit: string;
  status: ConstructionMovementEnum;
  work: { id: string; workName: string };
  client: {
    id: string;
    fantasyName: string;
    corporateName: string;
    fullName: string;
  };
  employee: {
    id: string;
    fullName: string;
    corporateName: string | null;
    fantasyName: string | null;
  };
  sectorEntity: {
    id: string;
    name: string;
  };
  categoryEntity: {
    id: string;
    name: string;
  };
}

export function ListagemMovimentaoesFinanceira() {
  const [totais, setTotais] = useState<any>(null);
  const [lista, setLista] = useState<any>(null);
  const [open, setOpen] = useState(false);
    const { showLoader, hideLoader } = useLoader();

  // Filtros do modal
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const dateInicio = new Date(
    firstDay.getTime() - firstDay.getTimezoneOffset() * 60000
  )
    .toISOString()
    .slice(0, 10);

  const [dateStart, setDateStart] = useState(dateInicio);
  const [dateEnd, setDateEnd] = useState(today);
  const [workIds, setWorkIds] = useState<string[]>([]);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [movementTypes, setMovementTypes] = useState<string[]>([]);

  const [query, setQuery] = useState<string>();

  const [clientesInput, setClientesInput] = useState<any[]>([]);
  const [obras, setObras] = useState<any[]>([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const [page, setPage] = useState(0); // MUI começa em 0
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openModalDetalhe, setOpenModalDetalhe] = useState(false);
  const [idMovimentacao, setIdMovimentacao] = useState<string>("");
  const [detalheMovimentacao, setDetalheMovimentacao] = useState<any>(null);

  const navigate = useNavigate();

  // Carrega clientes, obras e faz a consulta inicial só uma vez
  useEffect(() => {
    const fetchInitialData = async () => {
      const clientesData = await baseApiService.get("/client/select");
      const clientes = clientesData.data.data || [];
      setClientesInput(clientes);
      const todosClientesIds = clientes.map((cli: any) => cli.id);
      setClientIds(todosClientesIds);

      const obrasData = await baseApiService.get(
        `/work/select?campo_pesquisa=id_client&tipo_pesquisa=in&valor_pesquisa=${todosClientesIds.join(
          ","
        )}`
      );
      const obrasList = obrasData.data.data || [];
      setObras(obrasList);
      setWorkIds(obrasList.map((obra: any) => obra.id));

      setMovementTypes(["income", "outcome"]);

      // Consulta inicial (apenas uma vez)
      const queryDefault = `campo_pesquisa=date_movement&tipo_pesquisa=entre&valor_pesquisa=${dateInicio},${today}&campo_pesquisa=work_id&tipo_pesquisa=in&valor_pesquisa=${obrasList
        .map((obra: any) => obra.id)
        .join(
          ","
        )}&campo_pesquisa=client_id&tipo_pesquisa=in&valor_pesquisa=${todosClientesIds.join(
        ","
      )}&campo_pesquisa=movement_type&tipo_pesquisa=in&valor_pesquisa=income,outcome`;
      carregarTotais(queryDefault);
      carregarLista(queryDefault);
      setFirstLoad(false);
    };
    fetchInitialData();
  }, []);

  // Sempre que clientIds mudar, atualiza obras e seleciona todas se for o primeiro carregamento
  useEffect(() => {
    const fetchObras = async () => {
      if (clientIds.length === 0) {
        setObras([]);
        setWorkIds([]);
        return;
      }
      const obrasData = await baseApiService.get(
        `/work/select?campo_pesquisa=id_client&tipo_pesquisa=in&valor_pesquisa=${clientIds.join(
          ","
        )}`
      );
      const obrasList = obrasData.data.data || [];
      setObras(obrasList);
      if (firstLoad) {
        setWorkIds(obrasList.map((obra: any) => obra.id));
      }
    };
    fetchObras();
    // eslint-disable-next-line
  }, [clientIds]);

  const carregarTotais = async (query?: string) => {
    showLoader();
    try {
      const result = await financeiroService.totaisMovimentacoesObras(
        query || ""
      );
      setTotais(result);
    } catch (error) {
      console.error("Erro ao buscar obras:", error);
      setTotais(null);
    } finally {
      hideLoader();
    }
  };

  const carregarLista = async (
    query?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    showLoader();
    try {
      const result = await financeiroService.listaMovimentacoesObras(
        query || "",
        pagina,
        limite
      );
      setLista(result);
      setTotalCount(result.meta?.totalItems || 0);
    } catch (error) {
      console.error("Erro ao buscar obras:", error);
      setLista(null);
    } finally {
      hideLoader();
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarLista(query, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const handleOpenModal = () => setOpen(true);

  // Monta a query string conforme filtros
  const handleFiltrar = () => {
    let query = "";

    if (dateStart && dateEnd) {
      query += `campo_pesquisa=date_movement&tipo_pesquisa=entre&valor_pesquisa=${dateStart},${dateEnd}&`;
    }

    if (workIds.length) {
      query += `campo_pesquisa=work_id&tipo_pesquisa=in&valor_pesquisa=${workIds.join(
        ","
      )}&`;
    }

    if (clientIds.length) {
      query += `campo_pesquisa=client_id&tipo_pesquisa=in&valor_pesquisa=${clientIds.join(
        ","
      )}&`;
    }

    if (movementTypes.length) {
      query += `campo_pesquisa=movement_type&tipo_pesquisa=in&valor_pesquisa=${movementTypes.join(
        ","
      )}&`;
    }

    // Remove possível & final
    query = query.replace(/&$/, "");

    setQuery(query);
    carregarTotais(query);
    carregarLista(query);
    setOpen(false);
  };

  const handleDetalhar = (idMovimentacaoParam: string) => {
    if (idMovimentacaoParam && idMovimentacaoParam.trim() !== "") {
      setIdMovimentacao(idMovimentacaoParam);
      setOpenModalDetalhe(true);
    }
  };

  const theme = useTheme();
  const des = useMediaQuery(theme.breakpoints.down(1340));
  const tb = useMediaQuery(theme.breakpoints.down(1000));
  const mb = useMediaQuery(theme.breakpoints.down(720));

  return (
    <BaseLayout
      title="Gestão Financeira Obras"
      barraDeFerramentas={
        <FerramanetasDaListagem
          textoBotaoNovo="Gerar Nova Movimentação"
          mostrarInputBusca={false}
          mostraBotaoFiltro={true}
          mostraBotaoNovo={false}
          aoClicarBotaoFiltro={handleOpenModal}
          aoClicarBotaoNovo={() =>
            navigate("/financeiro/gerar-movimentacoes-obras")
          }
        />
      }
    >
      <Box height="100%">
        <FiltroMovimentacoesModal
          open={open}
          onClose={() => setOpen(false)}
          dateStart={dateStart}
          setDateStart={setDateStart}
          dateEnd={dateEnd}
          setDateEnd={setDateEnd}
          clientesInput={clientesInput}
          clientIds={clientIds}
          setClientIds={setClientIds}
          obras={obras}
          workIds={workIds}
          setWorkIds={setWorkIds}
          movementTypes={movementTypes}
          setMovementTypes={setMovementTypes}
          mostraInputOrigem={false}
          onFiltrar={handleFiltrar}
        />

        <ModalDetalhamentoMovimentacaoFinanceira
          open={openModalDetalhe}
          onClose={() => setOpenModalDetalhe(false)}
          idMovimentacao={idMovimentacao}
        />

        {/* Totais */}
        {totais && totais.previousPeriod !== undefined ? (
          <Box
            display="grid"
            gridTemplateColumns={
              mb ? "1fr" : des ? "1fr 1fr" : "1fr 1fr 1fr 1fr"
            }
            gap="24px"
            textAlign="center"
            mb={4}
          >
            <Box
              boxShadow="0 4px 16px rgba(0,0,0,0.10)"
              borderRadius={3}
              p={mb ? 2 : 3}
              bgcolor="#f8fafc"
            >
              <Typography variant="subtitle2" color="text.secondary">
                Período Anterior
              </Typography>
              <Typography
                variant={tb ? "h5" : "h4"}
                fontWeight={700}
                color="primary.main"
                mt={1}
              >
                {totais.previousPeriod.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Box>
            <Box
              boxShadow="0 4px 16px rgba(0,0,0,0.10)"
              borderRadius={3}
              p={mb ? 2 : 3}
              bgcolor="#f0fdf4"
            >
              <Typography variant="subtitle2" color="text.secondary">
                Entradas
              </Typography>
              <Typography
                variant={tb ? "h5" : "h4"}
                fontWeight={700}
                color="#22c55e"
                mt={1}
              >
                {totais.totalIncome.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Box>
            <Box
              boxShadow="0 4px 16px rgba(0,0,0,0.10)"
              borderRadius={3}
              p={mb ? 2 : 3}
              bgcolor="#fef2f2"
              border="1.5px solid #ef4444"
            >
              <Typography variant="subtitle2" color="text.secondary">
                Saídas
              </Typography>
              <Typography
                variant={tb ? "h5" : "h4"}
                fontWeight={700}
                color="#ef4444"
                mt={1}
              >
                -{" "}
                {totais.totalOutcome.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Box>
            <Box
              boxShadow="0 4px 16px rgba(0,0,0,0.10)"
              borderRadius={3}
              p={mb ? 2 : 3}
              bgcolor="#f8fafc"
            >
              <Typography variant="subtitle2" color="text.secondary">
                Saldo
              </Typography>
              <Typography
                variant={tb ? "h5" : "h4"}
                fontWeight={700}
                color="primary.main"
                mt={1}
                width="100%"
              >
                {totais.balance.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Box>
          </Box>
        ) : (
          <p>Carregando totais...</p>
        )}

        <Box
          display={mb ? "none" : "flex"}
          flexDirection="column"
          paddingBottom="40px"
        >
          <PaginacaoListagem
            totalCount={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
          <TabelaDaListagem
            ariaLabel="tabela de movimentações"
            headTable={[
              "Data",
              "Status",
              "Obra",
              "Cliente",
              "Funcionário",
              "Categoria",
              "Tipo",
              "Quantidade",
              "Descrição",
              "Valor (R$)",
              "Ações",
            ]}
            columnWidths={[
              100, 120, 100, 120, 120, 120, 100, 100, 180, 100, 100,
            ]}
            tableBody={
              lista?.items.length > 0 ? (
                lista?.items.map((mov: MovimentacaoObra) => (
                  <TableRow hover key={mov.id} sx={{ height: 40 }}>
                    <TableCell sx={{ minWidth: 100 }}>
                      {formatDateBR(formatDate(mov?.dateMovement))}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.status === ConstructionMovementEnum.GENERATED ? (
                        <Chip
                          label="Gerado"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="Cancelado"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.work?.workName}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.client?.fantasyName ||
                        mov.client?.corporateName ||
                        mov.client?.fullName}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.employee?.fullName ||
                        mov.employee?.fantasyName ||
                        mov.employee?.corporateName ||
                        "--"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {mov.categoryEntity?.name || "--"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }} align="center">
                      {tiposMovimentacao[mov?.movementType] || "--"}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }} align="center">
                      {mov.quantityToPay != null
                        ? `${mov.quantityToPay} ${
                            modalidade[mov.unit] || mov.unit || ""
                          }`
                        : "--"}
                    </TableCell>
                    <TableCell
                      sx={{
                        minWidth: 180,
                        maxWidth: 220,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {mov.description || "--"}
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      {Number(mov.value).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                        minimumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell align="left" sx={{ minWidth: 100 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleDetalhar(mov.id)}
                      >
                        <Icon>visibility</Icon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10}>
                    <RetornoDaListagemVazia />
                  </TableCell>
                </TableRow>
              )
            }
          />
        </Box>
      </Box>
    </BaseLayout>
  );
}
